import { useMsal } from '@azure/msal-react'
import {
  Badge,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionInfo } from '../../contexts/SessionContext'
import { fetchOrganizationTransactions } from '../../data/organization.service'
import { PaymentStatus } from '../../models/Transactions/PaymentStatus'
import { TransactionSummary } from '../../models/Transactions/TransactionSummary'
import { convertServerDateToLocal } from '../../utils/dateUtils'
import { formatNumber } from '../../utils/displayUtils'
import DashboardHeader from './DashboardHeader'

const LoadingSkeleton = () => (
  <Stack>
    <Group
      justify='space-between'
      align='center'
      h={70}
      px={24}
      style={{ boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)' }}
    >
      <Title fw={500} fz={{ base: 'h3', sm: 'h2' }}>
        Transaction History
      </Title>
    </Group>
    <Stack mt='md' gap='xl' px={24}>
      {[1, 2, 3].map((idx) => (
        <Paper key={idx} p='md' radius='md' withBorder>
          <Group justify='space-between' align='flex-start' wrap='nowrap'>
            <Stack gap={2}>
              <Skeleton height={24} width={200} />
              <Skeleton height={20} width={150} />
              <Skeleton height={16} width={180} />
            </Stack>
            <Stack gap={2} align='flex-end'>
              <Skeleton height={24} width={100} />
              <Skeleton height={20} width={140} />
              <Skeleton height={16} width={120} />
            </Stack>
          </Group>
        </Paper>
      ))}
    </Stack>
  </Stack>
)

const TransactionHistoryPage = () => {
  const { session } = useSessionInfo()
  const { instance } = useMsal()
  const [transactions, setTransactions] = useState<TransactionSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.organizationId) return
      setIsLoading(true)
      try {
        const data = await fetchOrganizationTransactions(
          session.organizationId,
          instance,
        )
        setTransactions(data)
      } catch (error) {
        notifications.show({
          title: 'Error loading transactions',
          message: 'Unable to load transaction history. Please try again.',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [session?.organizationId, instance])

  const getStatusBadge = (status: PaymentStatus) => {
    return (
      <Badge color={status === PaymentStatus.Paid ? 'green' : 'yellow'}>
        {status}
      </Badge>
    )
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  return (
    <Stack>
      <DashboardHeader title=' Transaction History' />
      <Stack mt='md' gap='xl' px={24}>
        {transactions.length === 0 && (
          <Text c='dimmed' ta='center'>
            No transactions found.
          </Text>
        )}
        {transactions.map((t) => (
          <Paper
            key={t.transactionId}
            p='md'
            radius='md'
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() =>
              navigate(`/dashboard/transactions/${t.transactionId}`)
            }
          >
            <Group justify='space-between' align='flex-start' wrap='nowrap'>
              <Stack gap={2}>
                <Text fw={600} fz='lg'>
                  {t.isAllLocations
                    ? (t.bundleName ?? 'All Locations Bundle')
                    : t.locationNames.join(', ')}
                </Text>
                <Stack gap={2}>
                  {t.isAllLocations ? (
                    <Text size='sm' c='dimmed'>
                      All Locations Bundle
                    </Text>
                  ) : (
                    <Text size='sm' c='dimmed'>
                      {t.advertisementCount} Advertisement
                      {t.advertisementCount !== 1 ? 's' : ''}
                    </Text>
                  )}
                  {t.designRequestCount > 0 && (
                    <Text size='sm' c='dimmed'>
                      {t.designRequestCount} Design Request
                      {t.designRequestCount !== 1 ? 's' : ''}
                    </Text>
                  )}
                </Stack>
                <Group gap={8}>
                  <Text size='xs' c='dimmed'>
                    Order made on{' '}
                    {convertServerDateToLocal(t.orderDate)?.toLocaleDateString(
                      'en-US',
                      {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      },
                    )}
                  </Text>
                </Group>
              </Stack>
              <Stack gap={2} align='flex-end'>
                {getStatusBadge(t.paymentStatus)}
                <Group gap={4} align='center'>
                  <Text fw={700} fz='lg'>
                    ${formatNumber(t.amountPaid)}
                  </Text>
                  <Text size='sm' c='dimmed'>
                    of ${formatNumber(t.totalAmount)}
                  </Text>
                </Group>
                <Text size='sm' fw={500}>
                  {t.lastPaymentDate &&
                    convertServerDateToLocal(
                      t.lastPaymentDate,
                    )?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                </Text>
                {t.nextPaymentDate && (
                  <Text size='xs' c='dimmed'>
                    Next Payment:{' '}
                    {convertServerDateToLocal(
                      t.nextPaymentDate,
                    )?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                )}
              </Stack>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  )
}

export default TransactionHistoryPage
